import { Checkbox } from '@rebass/forms'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import FCheckBox from 'components/BaseComponents/FCheckBox'
import FImage from 'components/BaseComponents/FImage'
import SBox from 'components/opinionless/SBox'
import SInputError from 'components/opinionless/SInputErrorV2'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'

import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { HOUSEHOLD_MEMBER_RELATIONSHIP } from 'containers/Enrollment/HouseholdIncome/HouseholdIncomeMembersDOB'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import { SStyled, useSTheme } from 'styles/theme'
import { useSaveHouseholdMembersMutation, useUpdateBlindDisabledMutation } from 'types/graphql'
import { getAge } from 'utils/date'
import { ga4_useTitle } from 'utils/ga4'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useHouseholdMembers } from 'v2/hooks/useHouseholdMembers'
import style from './style.module.scss'

interface Props {
    setCurrentStep?: (number) => void
    applicationId: string
    personId: string
    fontFamily: any
    onNextStep?: any
    onPrevious?: any
    updatePercentage: any
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`
export const CheckboxV2 = SStyled(Checkbox)`
    ${(p) => `
    @media screen and (max-width: ${p.theme.breakpoints[3]}) {
        margin-right: 15px;
    }
    `}
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-right: 7px;
            width: 45px;
            height: 45px;
        }
    `}
`
export const SCheckBoxLabel = SStyled(SBox)`
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-bottom: 7px;
        }
    `}
`

const HouseholdIncomeBlindDisabledMembers: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const applicationContext = useContext(ApplicationContext)

    const {
        loading: householdMembersLoading,
        error: householdMembersError,
        refetch: householdMembersRefetch,
    } = useHouseholdMembers()
    useApplicationBasicInfo()

    useEffect(() => {
        householdMembersRefetch()
    }, [householdMembersRefetch])

    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const fontFamily = props.fontFamily

    const { handleSubmit, errors, setError } = useForm()

    const [updateBlindDisabled, { loading: updateBlindDisabledLoading }] = useUpdateBlindDisabledMutation()
    const [saveHouseholdMembers, { loading: saveHouseholdMembersLoading }] = useSaveHouseholdMembersMutation()
    const [householdMembersDetails, setHouseholdMembersDetails] = useState([])
    const [applicantBlindDisabled, setApplicantBlindDisabled] = useState(null)

    const [loading, setLoading] = useState(true)

    ga4_useTitle('Household Income Blind Disabled Members')

    useEffect(() => {
        if (!householdMembersLoading) {
            setLoading(false)
            setHouseholdMembersDetails(
                applicationContext.householdMembers.map((member) => ({
                    id: member.id,
                    name: member.name,
                    relation: member.relation,
                    dob: member.dob,
                    blindDisabled: member.blindDisabled,
                }))
            )
            setApplicantBlindDisabled(applicationContext?.profile?.blindDisabled)
        }
    }, [applicationContext.householdMembers])

    const handleBlindDisabledSelection = () => {
        updateBlindDisabled({
            variables: {
                verificationCode: applicationContext.profile.person.id,
                blindDisabled: applicantBlindDisabled,
            },
        }).then((response) => {
            if (response.data.updateBlindDisabled.ok === true) {
                saveHouseholdMembers({
                    variables: {
                        personId: applicationContext.profile.person.id,
                        householdMembers: householdMembersDetails,
                    },
                }).then((response) => {
                    if (response.data.saveHouseholdMembers.success) {
                        props.nextStep()
                    } else {
                        setError('householdMembersBlindDisabled', { type: 'oops', message: t('error') })
                    }
                })
            } else {
                setError('householdMembersBlindDisabled', { type: 'oops', message: t('error') })
            }
        })
    }

    const handleApplicantBlindDisabledSelection = (blind_disabled: boolean) => {
        setApplicantBlindDisabled(blind_disabled)
    }

    const handleMemberBlindDisabledSelection = (index: number, blindDisabled: boolean) => {
        const updatedMembersDetails = householdMembersDetails.map((member, i) => ({
            ...member,
            blindDisabled: i === index ? blindDisabled : member.blindDisabled,
        }))

        setHouseholdMembersDetails(updatedMembersDetails)
    }

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.householdInfo,
        WIZARD_STEPS_MAPPING.householdInfo.blindDisabled,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    if (loading) {
        return null
    }
    return (
        <Container>
            <SBox as="form" onSubmit={handleSubmit(handleBlindDisabledSelection)}>
                <FButtonPrevious onClick={props.previousStep} />
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '800',
                        lineHeight: '140%',
                        color: '#202020',
                        fontSize: '16px',
                        marginBottom: '24px',
                    }}
                >
                    {t('long_beach_household_income_blind_disabled_members_subtitle')}
                </LBText>
                <div className={style.household_applicant_blind_disabled_container}>
                    <div className={style.household_applicant_blind_disabled_container__icon}>
                        <FImage imageUrl={'/lb_household_income_person.svg'} alt="person" />
                    </div>
                    <div className={style.household_applicant_blind_disabled_container__applicant_group}>
                        <LBText
                            sx={{
                                textAlignLast: 'left',
                                whiteSpace: 'pre-line',
                                fontFamily: fontFamily.body,
                                fontWeight: '700',
                                lineHeight: '140%',
                                color: theme.colors.lbBlue,
                                fontSize: '14px',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: t('long_beach_applicant_name_label_relationship_age', {
                                    name: `${applicationContext?.profile?.firstName} ${applicationContext?.profile?.lastName}`,
                                    relationship: t('long_beach_household_applicant_label'),
                                    age: getAge(applicationContext?.profile?.dob?.toString()),
                                }),
                            }}
                        />
                        <FCheckBox
                            name={'applicant_blind_disabled'}
                            id={'applicantBlindDisabled'}
                            value={'applicant_blind_dsabled'}
                            isChecked={applicantBlindDisabled}
                            text={t('long_beach_household_income_blind_disabled_option')}
                            onChange={() => handleApplicantBlindDisabledSelection(!applicantBlindDisabled)}
                        />
                    </div>
                </div>
                {householdMembersDetails.map((member, index) => (
                    <div className={style.household_member_blind_disabled_container} key={member.id}>
                        <div className={style.household_member_blind_disabled_container__icon}>
                            <FImage imageUrl={'/lb_household_income_person_bw.svg'} alt="person" />
                        </div>
                        <div className={style.household_member_blind_disabled_container__member_group}>
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '700',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '5px',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('long_beach_member_name_label_relationship_age', {
                                        name: `${member.name}`,
                                        relationship: t(`${HOUSEHOLD_MEMBER_RELATIONSHIP[member.relation]}`),
                                        age: `${getAge(member.dob)}`,
                                    }),
                                }}
                            />
                            <FCheckBox
                                name={`member_blind_disabled_${index}`}
                                id={`memberBlindDisabled_${index}`}
                                value={'member_blind_dsabled'}
                                isChecked={member?.blindDisabled || false}
                                text={t('long_beach_household_income_blind_disabled_option')}
                                onChange={() => handleMemberBlindDisabledSelection(index, !member.blindDisabled)}
                            />
                        </div>
                    </div>
                ))}

                <LBButton
                    type={'submit'}
                    id={'continueButton'}
                    loading={updateBlindDisabledLoading || saveHouseholdMembersLoading}
                    title={t('continue')}
                    width={'100%'}
                    mt={7}
                    mb={5}
                    textStyle={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontFamily: fontFamily.body,
                        color: theme.colors.white,
                    }}
                >
                    {t('save_and_continue')}
                </LBButton>
                {errors.householdMembersBlindDisabled && (
                    <SInputError
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                        forElementId={'continueButton'}
                    >
                        {t(errors.householdMembersBlindDisabled?.message)}
                    </SInputError>
                )}
            </SBox>
        </Container>
    )
}

export default HouseholdIncomeBlindDisabledMembers
